/**
 * Connexion par empreinte digitale (WebAuthn) — gestion depuis le profil.
 *
 * Le capteur d'empreinte du téléphone est appelé directement par le navigateur.
 * Rien à installer : ni application, ni modèle à télécharger, contrairement à la
 * reconnaissance faciale. La bibliothèque pèse une dizaine de kilo-octets.
 */

import React, { useState, useEffect } from 'react';
import { FaFingerprint, FaTrash, FaCheckCircle, FaShieldAlt } from 'react-icons/fa';
import { startRegistration, browserSupportsWebAuthn, platformAuthenticatorIsAvailable } from '@simplewebauthn/browser';
import toast from 'react-hot-toast';
import api from '../../api/axios';

function FingerprintSection() {
  const [appareils, setAppareils]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [enCours, setEnCours]       = useState(false);
  const [suppression, setSuppression] = useState(null);
  const [supporte, setSupporte]     = useState(null); // null = en cours de test

  const charger = () => {
    api.get('/webauthn/list')
      .then(({ data }) => setAppareils(data.credentials || []))
      .catch(() => setAppareils([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    charger();
    // Deux conditions : le navigateur connaît WebAuthn, ET l'appareil possède un
    // capteur intégré (empreinte ou reconnaissance du visage du système).
    if (!browserSupportsWebAuthn()) { setSupporte(false); return; }
    platformAuthenticatorIsAvailable()
      .then((dispo) => setSupporte(dispo))
      .catch(() => setSupporte(false));
  }, []);

  const enregistrer = async () => {
    setEnCours(true);
    try {
      const { data } = await api.post('/webauthn/register/options', {});
      // Ouvre le capteur du téléphone : c'est le système qui valide l'empreinte
      const reponse = await startRegistration({ optionsJSON: data.options });
      const nom = /Android/i.test(navigator.userAgent) ? 'Téléphone Android'
        : /iPhone|iPad/i.test(navigator.userAgent) ? 'iPhone'
        : 'Cet appareil';
      await api.post('/webauthn/register/verify', { response: reponse, deviceName: nom });
      toast.success('Empreinte enregistrée ! Tu peux maintenant te connecter d\'un doigt.');
      charger();
    } catch (err) {
      // L'utilisateur peut simplement avoir annulé la demande du système
      if (err?.name === 'NotAllowedError') {
        toast.error('Enregistrement annulé');
      } else if (err?.name === 'InvalidStateError') {
        toast.error('Cet appareil est déjà enregistré');
      } else {
        toast.error(err?.response?.data?.message || 'Enregistrement impossible sur cet appareil');
      }
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async (a) => {
    if (!window.confirm(`Retirer « ${a.device_name} » ? Tu ne pourras plus te connecter par empreinte depuis cet appareil.`)) return;
    setSuppression(a.id);
    try {
      await api.delete(`/webauthn/${a.id}`);
      toast.success('Appareil retiré');
      setAppareils((prev) => prev.filter((x) => x.id !== a.id));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setSuppression(null);
    }
  };

  // Inutile d'afficher la section sur un appareil qui n'en est pas capable
  if (supporte === false && appareils.length === 0) return null;

  return (
    <div className="rounded-3xl p-5 border border-white/10 mb-4" style={{ background: 'rgba(0,0,0,0.3)' }}>
      <div className="flex items-center gap-2 mb-1">
        <FaFingerprint className="text-qfl-gold" size={15} />
        <h3 className="text-qfl-gold font-black text-sm uppercase tracking-wider"
          style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          Connexion par empreinte
        </h3>
      </div>
      <p className="text-white/40 text-xs mb-4 leading-relaxed">
        Connecte-toi avec l'empreinte de ton téléphone, sans mot de passe.
      </p>

      <div className="flex items-start gap-2 p-3 rounded-xl mb-4"
        style={{ background: 'rgba(34,197,94,0.07)', border: '1px solid rgba(34,197,94,0.2)' }}>
        <FaShieldAlt className="text-green-400 flex-shrink-0 mt-0.5" size={11} />
        <p className="text-white/60 text-[11px] leading-relaxed">
          Ton empreinte ne quitte <strong className="text-white/80">jamais</strong> ton téléphone :
          elle est vérifiée par le téléphone lui-même, qui signe une preuve pour le serveur.
          C'est la méthode la plus sûre de l'application.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <div className="w-6 h-6 border-2 border-qfl-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {appareils.length > 0 && (
            <div className="space-y-2 mb-4">
              {appareils.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04]">
                  <FaCheckCircle className="text-green-400 flex-shrink-0" size={13} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-semibold truncate">{a.device_name}</p>
                    <p className="text-white/35 text-[10px]">
                      {a.last_used
                        ? `Dernière connexion : ${new Date(a.last_used).toLocaleDateString('fr-FR')}`
                        : 'Jamais utilisé'}
                    </p>
                  </div>
                  <button onClick={() => supprimer(a)} disabled={suppression === a.id}
                    className="text-red-400/60 hover:text-red-400 p-2 disabled:opacity-40">
                    <FaTrash size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {supporte === false ? (
            <p className="text-white/30 text-[11px] text-center">
              Cet appareil n'a pas de capteur compatible. Ouvre l'application sur ton téléphone pour en ajouter un.
            </p>
          ) : (
            <button
              onClick={enregistrer}
              disabled={enCours || supporte === null}
              className="w-full py-3 rounded-xl font-bold text-sm text-white/80 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <FaFingerprint size={14} />
              {enCours ? 'Suis les instructions de ton téléphone…'
                : appareils.length > 0 ? 'Ajouter cet appareil' : 'Activer la connexion par empreinte'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default FingerprintSection;
